/**
 * Shared trend calculation utilities
 * Used by useSiteManagerStatsQuery and useDashboardEnrichedQuery
 */

/**
 * Calculate percentage trend between current and previous period
 * Returns 0 if previous is 0, 100 if current > 0 and previous is 0
 */
export function calculateTrend(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

/**
 * Calculate trend with inverted logic (less = better, e.g., incidents)
 * Returns null if no previous data
 */
export function calculateInvertedTrend(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
}

/**
 * Calculate period date ranges for trend comparison
 */
export interface DateRange {
  start: Date;
  end: Date;
}

export interface PeriodRanges {
  current: DateRange;
  previous: DateRange;
}

export function getPeriodDateRanges(
  period: 'week' | 'biweek' | 'month',
  baseDate: Date = new Date()
): PeriodRanges {
  const today = new Date(baseDate);
  today.setHours(0, 0, 0, 0);

  let currentStart: Date;
  let currentEnd: Date = today;
  let previousStart: Date;
  let previousEnd: Date;

  switch (period) {
    case 'week':
      currentStart = new Date(today);
      currentStart.setDate(today.getDate() - 7);
      previousEnd = new Date(currentStart);
      previousEnd.setDate(previousEnd.getDate() - 1);
      previousStart = new Date(previousEnd);
      previousStart.setDate(previousStart.getDate() - 6);
      break;
    case 'biweek':
      currentStart = new Date(today);
      currentStart.setDate(today.getDate() - 14);
      previousEnd = new Date(currentStart);
      previousEnd.setDate(previousEnd.getDate() - 1);
      previousStart = new Date(previousEnd);
      previousStart.setDate(previousStart.getDate() - 13);
      break;
    case 'month':
    default:
      currentStart = new Date(today);
      currentStart.setMonth(today.getMonth() - 1);
      previousEnd = new Date(currentStart);
      previousEnd.setDate(previousEnd.getDate() - 1);
      previousStart = new Date(previousEnd);
      previousStart.setMonth(previousStart.getMonth() - 1);
      break;
  }

  return {
    current: { start: currentStart, end: currentEnd },
    previous: { start: previousStart, end: previousEnd },
  };
}

/**
 * Format date to ISO date string (YYYY-MM-DD)
 */
export function toISODateString(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Get trend label based on period
 */
export function getTrendLabel(period: 'week' | 'month' | 'quarter'): string {
  switch (period) {
    case 'week':
      return 'vs. sem. préc.';
    case 'quarter':
      return 'vs. trim. préc.';
    case 'month':
    default:
      return 'vs. mois préc.';
  }
}
